import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AmazonPriceInfo {
  price_gbp: number | null;
  previous_price_gbp: number | null;
  availability: string | null;
  price_last_checked: string | null;
  affiliate_link: string;
}

const ASIN_REGEX = /\/(?:dp|gp\/product)\/([A-Z0-9]{10})/i;

// Module-level cache so multiple articles in a session reuse data
let cache: Record<string, AmazonPriceInfo> | null = null;
let cacheAt = 0;
const TTL = 10 * 60 * 1000;

function extractAsinsFromHtml(html: string): string[] {
  const out = new Set<string>();
  const linkRegex = /href=["']([^"']*amazon[^"']*)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = linkRegex.exec(html))) {
    const asin = m[1].match(ASIN_REGEX);
    if (asin) out.add(asin[1].toUpperCase());
  }
  return Array.from(out);
}

export async function fetchAmazonPrices(asins: string[]): Promise<Record<string, AmazonPriceInfo>> {
  if (asins.length === 0) return {};
  const now = Date.now();
  if (cache && now - cacheAt < TTL) {
    const hit: Record<string, AmazonPriceInfo> = {};
    let allHit = true;
    for (const a of asins) {
      if (cache[a]) hit[a] = cache[a];
      else allHit = false;
    }
    if (allHit) return hit;
  }
  const { data, error } = await supabase.functions.invoke("amazon-price-lookup", { body: { asins } });
  if (error || !data?.prices) return {};
  cache = { ...(cache || {}), ...data.prices };
  cacheAt = now;
  return data.prices;
}

function priceChipHtml(info: AmazonPriceInfo): string {
  if (!info.price_gbp) return "";
  const drop = info.previous_price_gbp && info.previous_price_gbp > info.price_gbp;
  const oos = info.availability && info.availability !== "in_stock";
  const price = `£${info.price_gbp.toFixed(2)}`;
  const wasMarkup = drop ? ` <span style="text-decoration:line-through;opacity:0.7;margin-left:4px">£${info.previous_price_gbp!.toFixed(2)}</span>` : "";
  const oosMarkup = oos ? ` <span style="background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:9999px;font-size:0.75em;margin-left:6px">Limited stock</span>` : "";
  return ` <span class="amazon-price-chip" style="display:inline-flex;align-items:center;gap:4px;background:#16a34a;color:#fff;padding:2px 8px;border-radius:9999px;font-size:0.85em;font-weight:600;margin-left:6px;vertical-align:middle">${price}${wasMarkup}</span>${oosMarkup}`;
}

/** Walks the rendered article DOM and appends price chips after Amazon affiliate links. */
function injectPriceChipsIntoNode(root: HTMLElement, prices: Record<string, AmazonPriceInfo>) {
  const links = root.querySelectorAll<HTMLAnchorElement>('a[href*="amazon"]');
  links.forEach((a) => {
    if (a.dataset.amazonChipApplied === "1") return;
    const m = a.href.match(ASIN_REGEX);
    if (!m) return;
    const asin = m[1].toUpperCase();
    const info = prices[asin];
    if (!info) return;
    a.dataset.amazonChipApplied = "1";
    const tmp = document.createElement("span");
    tmp.innerHTML = priceChipHtml(info);
    while (tmp.firstChild) a.parentNode?.insertBefore(tmp.firstChild, a.nextSibling);
  });
}

export function useAmazonPriceAugment(rootRef: React.RefObject<HTMLElement | null>, html: string | null | undefined) {
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDone(false);
    if (!html || !rootRef.current) return;
    const asins = extractAsinsFromHtml(html);
    if (asins.length === 0) { setDone(true); return; }
    let cancelled = false;
    fetchAmazonPrices(asins).then((prices) => {
      if (cancelled || !rootRef.current) return;
      injectPriceChipsIntoNode(rootRef.current, prices);
      setDone(true);
    });
    return () => { cancelled = true; };
  }, [html, rootRef]);
  return done;
}
